// @flow
import * as React from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import { withOptimizely } from '@optimizely/react-sdk';
import { GlobalFooter } from '@nexus/fff-global-footer';
import { Helmet } from 'react-helmet';

//eslint-disable-next-line
import GlobalNav from 'shared-components/GlobalNav/GlobalNav';

import { requestAllData } from 'services/actions/campaignActions';
import {
  requestBestSellingProducts,
  requestProducts,
  requestFeaturedProducts,
  resetAllProducts,
} from 'services/actions/productsActions';
import {
  setSelectedSort,
  setProductRecAbKey,
} from 'services/actions/sortActions';
import {
  updateEnableSearch,
  resetSearch,
} from 'services/actions/searchActions';
import { selectIsCampaignOpen } from 'services/reducers/campaignReducer';

import {
  selectIsSocialEcomm,
  selectDisplaySocialBanner,
} from 'selectors/socialEcommSelectors';

import { isStaticCategory } from 'utils/categories';
import { VERSION, SORT_TYPE } from 'utils/campaign';
import { VIEW_SIZE } from 'utils/device';
import { trackOptimizelyExperiment } from 'utils/segment';

import SalePageLayout from 'Scenes/Sale/SalePageLayout/SalePageLayout';

import { Toast } from 'shared-components/BaseToast/Toast';

import withViewSize from './HOC/withViewSize/withViewSize';
import withRouteChangers from './HOC/withRouteChangers/withRouteChangers';
import withVersion from './HOC/withVersion/withVersion';
import withSelectedCategoryId from './HOC/withSelectedCategoryId/withSelectedCategoryId';
import withSelectedProductLink from './HOC/withSelectedProductLink/withSelectedProductLink';
import withOptimizelyFF from './HOC/withOptimizely/withOptimizelyFF';

import Scene from './Scene';
import ErrorBoundary from './shared-components/ErrorBoundary/ErrorBoundary';

import './index.css';

import type { WithRouteChangersInjectedProps } from './HOC/withRouteChangers/withRouteChangers';
import type { WithSelectedProductLinkInjectedProps } from './HOC/withSelectedProductLink/withSelectedProductLink';
import type { State, Sort } from './sharedTypes';
import { isPaginatedSortBy } from './utils/sort';
import env from './utils/env';

type InjectedProps = {|
  +version: string,
  +selectedCategoryId: string | null,
  ...WithRouteChangersInjectedProps,
  ...WithSelectedProductLinkInjectedProps,
  +flags: {|
    initialSort: Sort,
    addOnsEditRedirectPath: string,
    socialEcomm: boolean,
    disableAvatars: boolean,
    disableTeamCartsAndAvatars: boolean,
  |},
|};

type StateProps = {|
  +viewSize: string,
  +selectedSort: Sort | null,
  +userRole: string | null,
  +userPlanCode: string | null,
  +userId: number | null,
  +loggedIn: boolean,
  +campaignIsOpen: boolean,
  +isSocialEcomm: boolean,
  +displaySocialBanner: boolean,
|};

type DispatchProps = {|
  +requestProducts: typeof requestProducts,
  +requestAllData: typeof requestAllData,
  +setSelectedSort: typeof setSelectedSort,
  +setProductRecAbKey: typeof setProductRecAbKey,
  +updateEnableSearch: typeof updateEnableSearch,
  +resetSearch: typeof resetSearch,
  +resetAllProducts: typeof resetAllProducts,
|};

type Props = {|
  ...InjectedProps,
  ...StateProps,
  ...DispatchProps,
|};

class App extends React.PureComponent<Props> {
  constructor(props) {
    super(props);
    this.state = {};
  }

  componentDidMount() {
    this.fetchAppData();
    this.setShowSearch();
  }

  componentDidUpdate(prevProps: Props) {
    this.handleOptimizelyTracking();
    this.redirectToOopsOnFlag();
    this.fetchProductsOnCategoryOrSortChange(prevProps);
    this.fetchAppDataOnVersionChange(prevProps);
    this.handleFeaturedProducts(prevProps);
    this.handleInitialSort(prevProps);
    this.handleProductRecommendation(prevProps);
    this.handleCategoryChange(prevProps);
  }

  handleFeaturedProducts = (prevProps) => {
    const {
      campaignIsOpen,
      optimizelyFeaturedUIDecision,
      version,
      requestFeaturedProducts,
      selectedCategoryId,
      flags,
      loggedIn,
    } = this.props;
    const { rateAndReview: rateAndReviewFF } = flags;
    const featuredProductsEnabled = optimizelyFeaturedUIDecision.enabled;
    const featuredProductsVersion =
      version === VERSION.edit || version === VERSION.shop;
    const categoryToRequest = isStaticCategory(selectedCategoryId)
      ? null
      : selectedCategoryId;
    if (
      campaignIsOpen &&
      (prevProps.optimizelyFeaturedUIDecision !==
        optimizelyFeaturedUIDecision ||
        prevProps.selectedCategoryId !== selectedCategoryId) &&
      featuredProductsEnabled &&
      featuredProductsVersion &&
      loggedIn
    ) {
      requestFeaturedProducts(categoryToRequest, rateAndReviewFF);
    }
  };

  redirectToOopsOnFlag = () => {
    const { addOnsEditRedirectPath } = this.props.flags;
    if (addOnsEditRedirectPath) {
      window.location = addOnsEditRedirectPath;
    }
  };

  setShowSearch = () => {
    const { version, updateEnableSearch } = this.props;
    if (version === 'edit' || version === 'add-ons' || version === 'shop') {
      updateEnableSearch(true);
    }
  };

  handleInitialSort = (prevProps: Props) => {
    const { optimizelySortDecision, setSelectedSort, loggedIn, version } =
      this.props;
    const { sortType } = optimizelySortDecision;
    const isPublicShop = !loggedIn && version === VERSION.shop;
    if (prevProps.optimizelySortDecision !== optimizelySortDecision) {
      const sortValue = isPublicShop
        ? SORT_TYPE.bestSellingRefills
        : sortType || SORT_TYPE.default;
      setSelectedSort(sortValue);
    }
  };

  handleProductRecommendation = (prevProps: Props) => {
    const { optimizelyProductRecommendationDecision, setProductRecAbKey } =
      this.props;
    if (
      prevProps.optimizelyProductRecommendationDecision !==
      optimizelyProductRecommendationDecision
    ) {
      setProductRecAbKey(
        optimizelyProductRecommendationDecision.productRecommendation
      );
    }
  };

  fetchProductsOnCategoryOrSortChange = (prevProps: Props) => {
    const {
      campaignIsOpen,
      selectedCategoryId,
      requestProducts,
      selectedSort,
      productRecAbKey,
      selectedProductLink,
      resetSearch,
      requestBestSellingProducts,
      version,
      flags,
      resetAllProducts,
    } = this.props;

    const {
      rateAndReview: rateAndReviewFF,
      alternateSubscriptions: alternateSubscriptionsFF,
      publicSubscriptions: publicSubscriptionsFF,
    } = flags;
    const didCampaignOpenUpdate = prevProps.campaignIsOpen !== campaignIsOpen;
    const didSelectedCategoryIdUpdate =
      prevProps.selectedCategoryId !== selectedCategoryId;
    const didSelectedSortUpdate = prevProps.selectedSort !== selectedSort;
    const didVersionUpdate = prevProps.version !== version;

    if (
      campaignIsOpen &&
      // if selectedSort is null, we're still waiting for the initial selected sort
      // to be set based on LD feature flag
      selectedSort !== null &&
      (didSelectedCategoryIdUpdate ||
        didSelectedSortUpdate ||
        didCampaignOpenUpdate ||
        didVersionUpdate)
    ) {
      if (
        !isPaginatedSortBy(selectedSort) &&
        !isStaticCategory(selectedCategoryId)
      ) {
        requestBestSellingProducts({
          selectedCategoryId,
          selectedProductLink,
          selectedSort,
          productRecAbKey,
          rateAndReviewFF,
          alternateSubscriptionsFF,
          publicSubscriptionsFF,
        });
      } else {
        requestProducts({
          offset: 0,
          selectedCategoryId,
          selectedSort,
          selectedProductLink,
          rateAndReviewFF,
          alternateSubscriptionsFF,
          publicSubscriptionsFF,
        });
      }
      resetSearch();
      resetAllProducts();
    }
  };

  fetchAppData = () => {
    const { refreshLocationState, requestAllData, userRole, flags } =
      this.props;
    refreshLocationState();
    //this requests user and campaign data
    requestAllData(userRole, flags);
  };

  fetchAppDataOnVersionChange = (prevProps: Props) => {
    const { version } = this.props;
    if (version !== prevProps.version) {
      // refetch data when version changes
      this.fetchAppData();
      this.setInitialSort();
      this.setShowSearch();
    }
  };

  handleOptimizelyTracking = () => {
    const {
      userId,
      campaignId,
      campaignIsOpen,
      optimizelySortDecision,
      optimizelySortReady,
      optimizelyProductRecommendationDecision,
      optimizelyProductRecommendationReady,
      optimizelyFlashSaleRedesign,
      optimizelyFlashSaleRedesignReady,
      fetchingCampaign,
      userPlanCode,
    } = this.props;
    const handleTracking = (decisionReady, decisionData) => {
      const { featureFlagName } = decisionData;
      const flagTracked = `${featureFlagName}Tracked`;
      // track Experiment Viewed and identify only if the campaign is open and we are running an A/B test
      if (
        !fetchingCampaign &&
        decisionReady &&
        campaignIsOpen &&
        !this.state[flagTracked]
      ) {
        trackOptimizelyExperiment(
          decisionData,
          userId,
          campaignId,
          userPlanCode
        );
        this.setState({ [flagTracked]: true });
      }
    };
    handleTracking(optimizelySortReady, optimizelySortDecision);
    handleTracking(
      optimizelyProductRecommendationReady,
      optimizelyProductRecommendationDecision
    );
    handleTracking(
      optimizelyFlashSaleRedesignReady,
      optimizelyFlashSaleRedesign
    );
  };

  // scroll to top when changing category
  handleCategoryChange = (prevProps) => {
    const { selectedCategoryId } = this.props;
    const didSelectedCategoryIdUpdate =
      prevProps.selectedCategoryId !== selectedCategoryId;
    if (didSelectedCategoryIdUpdate) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  };

  render() {
    const {
      viewSize,
      version,
      campaignIsOpen,
      flags,
      isSocialEcomm,
      displaySocialBanner,
    } = this.props;

    const { socialEcomm: socialEcommFF } = flags;
    const isEditPage = version === VERSION.edit;
    const hideChatWidget =
      (isEditPage && socialEcommFF) || viewSize === VIEW_SIZE.small;

    return (
      <>
        <Helmet>
          <title>FabFitFun - Beauty, Fitness, Lifestyle</title>
          <meta
            property="description"
            content="Delivering happiness and well-being to everyone, everywhere Join FabFitFun today"
          />
          <meta
            property="og:title"
            content="FabFitFun - Beauty, Fitness, Lifestyle"
          />
        </Helmet>
        {isSocialEcomm && (
          <Helmet>
            <meta property="og:description" content="Shop With Friends" />
            <meta property="og:type" content="website" />
            <meta property="fb:app_id" content={env.fbAppId} />
            <meta property="og:url" content="https://www.fabfitfun.com/edit" />
            <meta
              property="og:image"
              content="https://static.fabfitfun.com/wp-content/uploads/2021/10/YoBfhIw-2_1633479054.263_1633479054.5988.jpeg"
            />
          </Helmet>
        )}
        <SalePageLayout
          viewSize={viewSize}
          displaySocialBanner={displaySocialBanner}
        >
          <ErrorBoundary>
            <GlobalNav stickyCartPage={campaignIsOpen} showMobileSearchBar />
          </ErrorBoundary>
          <Scene
            version={version}
            campaignIsOpen={campaignIsOpen}
            isSocialEcomm={isSocialEcomm}
            displaySocialBanner={displaySocialBanner}
          />
          <ErrorBoundary>
            <GlobalFooter hideChatWidget={hideChatWidget} />
          </ErrorBoundary>
        </SalePageLayout>
        {isSocialEcomm && <Toast />}
      </>
    );
  }
}

const mapStateToProps = (state: State, props: InjectedProps): StateProps => ({
  viewSize: state.viewState.viewSize,
  selectedSort: state.sortState.selectedSort,
  productRecAbKey: state.sortState.productRecAbKey,
  userRole: state.userState.role,
  userPlanCode: state.userState.planCode,
  userId: state.userState.userId,
  loggedIn: state.userState.loggedIn,
  campaignId: state.campaignState.campaignId,
  campaignIsOpen: selectIsCampaignOpen(state),
  isSocialEcomm: selectIsSocialEcomm(state, props),
  displaySocialBanner: selectDisplaySocialBanner(state, props),
  fetchingCampaign: state.campaignState.fetchingCampaign,
  displayedProductIds: state.productsState.displayedProductIds,
});

const mapDispatchToProps = {
  requestProducts,
  requestAllData,
  setSelectedSort,
  setProductRecAbKey,
  updateEnableSearch,
  resetSearch,
  requestBestSellingProducts,
  requestFeaturedProducts,
  resetAllProducts,
};

export default compose(
  withViewSize,
  withRouteChangers,
  withVersion,
  withSelectedCategoryId,
  withSelectedProductLink,
  withLDConsumer(),
  withOptimizelyFF,
  withOptimizely,
  connect(mapStateToProps, mapDispatchToProps)
)(App);
